<template>
  <header class="app-header">
    <div class="app-header__top">
      <div class="app-header__left">
        <button
          class="app-header__burger"
          type="button"
          :aria-label="$t('nav.mainNavigation')"
          :aria-expanded="layoutStore.sidebarOpen"
          @click="handleToggle"
        >
          <span />
          <span />
          <span />
        </button>

        <RouterLink
          class="app-header__brand"
          :to="isAuthenticated || profileLoading ? '/dashboard' : '/'"
          :aria-label="$t(isAuthenticated || profileLoading ? 'nav.home' : 'nav.titles.landing')"
        >
          <div class="app-header__logo">
            <GonkaGameLogo />
          </div>
        </RouterLink>

        <nav class="app-header__menu">
          <AppNavigationItem
            v-for="item in mainNavigation"
            :key="item.name"
            class="app-header__menu-item"
            :data-nav-name="item.name"
            :class="{
              'app-header__menu-item--active': isMainActive(item),
              'app-header__menu-item--disabled': item.disabled,
            }"
            :label="$t(item.label)"
            :active="isMainActive(item)"
            :disabled="item.disabled"
            @click="handleMainNavigate(item)"
          >
            <span v-if="item.tag" class="app-header__menu-tag">{{ $t(item.tag) }}</span>
          </AppNavigationItem>
        </nav>
      </div>

      <div class="app-header__actions">
        <!-- Hidden for design-system v1 (dark-only, ТЗ Gonka §7); re-enable in v2 -->
        <ThemeToggle v-if="route.meta.public" />

        <template v-if="isAuthenticated || profileLoading">
          <RouterLink
            class="app-header__icon-button"
            :title="walletTooltip"
            to="/wallet"
          >
            <i class="app-header__icon-font zmdi zmdi-balance-wallet" />
          </RouterLink>

          <button
            class="app-header__icon-button"
            type="button"
            @click="handleNotificationsClick"
            :aria-label="$t('notifications.title')"
          >
            <i class="app-header__icon-font zmdi zmdi-notifications" />
            <span class="app-header__icon-dot" v-if="notificationsCount > 0">{{ notificationsCount }}</span>
          </button>


          <div class="app-header__profile-wrapper">
            <RouterLink
              class="app-header__avatar"
              to="/profile"
            >
            <span v-if="!avatarPhoto && profileLoading" class="app-header__avatar-skeleton" />
            <img
              v-else-if="avatarPhoto"
              :src="avatarPhoto"
              :alt="$t('nav.profile')"
            />
            <span v-else class="app-header__avatar-placeholder">{{ profileInitials }}</span>
          </RouterLink>
            
            <div v-if="adminReturnToken" class="app-header__admin-dropdown">
              <button class="app-header__admin-return-btn" @click="returnToAdmin">
                <i class="zmdi zmdi-sign-in"/>
                {{ $t('admin.returnToAdmin') }}
              </button>
            </div>
          </div>
        </template>
        <template v-else>
          <button class="auth-btn-text" @click="openLogin">{{ $t('auth.login') }}</button>
          <button class="auth-btn-primary" @click="openRegistration">
            <i class="zmdi zmdi-flash" />
            <span>{{ $t('auth.start') }}</span>
          </button>
        </template>
      </div>
    </div>

    <div
      v-if="secondaryNavigationItems.length"
      class="app-header__secondary"
    >
      <nav class="app-header__tabs">
        <button
          v-for="item in secondaryNavigationItems"
          :key="item.name"
          class="app-header__tab"
          :class="{
            'app-header__tab--active': activeSecondary === item.name,
            'app-header__tab--disabled': item.disabled,
          }"
          type="button"
          @click="handleSecondaryNavigate(item)"
        >
          <span class="app-header__tab-label">{{ $t(item.label) }}</span>
          <span v-if="item.badge" class="app-header__tab-badge">{{ $t(item.badge) }}</span>
        </button>
      </nav>
    </div>
    <RegistrationModal 
      v-if="showRegistration"
      v-model="showRegistration" 
      @switch-to-login="openLogin"
      @switch-to-login-with-code="openLoginWithCode"
      @switch-to-login-totp="openLoginWithTotp"
    />
    
    <LoginModal 
      v-model="showLogin"
      :initial-email="loginEmail"
      :auto-send-code="autoSendCode"
      :telegram-totp="loginTotp"
      @switch-to-register="openRegistration"
      @switch-to-recovery="openRecovery"
    />

    <PasswordRecoveryModal
      v-model="showRecovery"
      :initial-email="recoveryEmail"
      @switch-to-login="openLogin"
    />
  </header>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useNotifications } from '@space/notifications'
import { createTokenStorage } from '@space/shared-api-client'

import GonkaGameLogo from '@/components/common/GonkaGameLogo.vue'
import AppNavigationItem from '@/components/navigation/AppNavigationItem.vue'
import ThemeToggle from '@/components/common/ThemeToggle.vue'
import { primaryNavigation, secondaryNavigation } from '@/shared/config/navigation'
import type { NavigationItem } from '@/shared/types/navigation'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { useLayoutStore } from '@/store/layoutStore'
import RegistrationModal from '@/modules/auth/components/RegistrationModal.vue'
import LoginModal from '@/modules/auth/components/LoginModal.vue'
import type { TelegramTotpSignal } from '@/modules/auth/services/TelegramLoginPollingService'
import PasswordRecoveryModal from '@/modules/auth/components/PasswordRecoveryModal.vue'
import type { TmaState } from '@/mini-app'

const router = useRouter()
const route = useRoute()
const profileStore = useProfileStore()
const notifications = useNotifications()
const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken'
const tokenStorage = createTokenStorage({ storageKey })

// Telegram Mini-App state (provided in main.ts)
const tmaState = inject<Ref<TmaState>>('tmaState', undefined as any)
const isInTelegram = inject<boolean>('isInTelegram', false)

// Wallet & Profile are intentionally omitted from the top nav — they are already
// reachable via the icon buttons on the right side of the header.
// Guests (pre-auth landing) get anchor links to the landing sections instead
// of app routes (тезисы по главной: «Как это работает / Награды / Реф. программа»).
const GUEST_NAVIGATION: NavigationItem[] = [
  { name: 'how', label: 'nav.howItWorks', route: '/#how' },
  { name: 'rewards', label: 'nav.rewards', route: '/#rewards' },
  { name: 'referral', label: 'nav.affiliate', route: '/#referral' },
]

const mainNavigation = computed(() =>
  isAuthenticated.value || profileLoading.value
    ? primaryNavigation.filter((item) => item.name !== 'wallet' && item.name !== 'profile')
    : GUEST_NAVIGATION,
)

const activeMain = computed<NavigationItem | null>(() => {
  const current = mainNavigation.value.find((item) => {
    if (!item.route) {
      return false
    }

    if (item.route === '/') {
      return route.path === '/'
    }

    return [item.route, ...(item.matchRoutes ?? [])].some(path => route.path === path || route.path.startsWith(`${path}/`))
  })

  return current ?? null
})

const secondaryGroupKey = computed(() => {
  if (activeMain.value?.name) {
    return activeMain.value.name
  }

  let matchedKey: string | null = null
  let longestMatchLength = -1

  Object.entries(secondaryNavigation).forEach(([key, items]) => {
    items.forEach((item) => {
      if (!item.route || !route.path.startsWith(item.route)) {
        return
      }

      if (item.route.length > longestMatchLength) {
        longestMatchLength = item.route.length
        matchedKey = key
      }
    })
  })

  return matchedKey
})

const secondaryNavigationItems = computed(() => {
  const key = secondaryGroupKey.value
  return key ? secondaryNavigation[key] ?? [] : []
})

const activeSecondary = computed(() => {
  const tabQuery = route.query.tab as string | undefined
  if (tabQuery) {
    return tabQuery
  }

  const bestMatch = secondaryNavigationItems.value.reduce<NavigationItem | null>((currentBest, item) => {
    // Check if route matches directly
    const routeMatches = item.route && route.path.startsWith(item.route)
    // Check if any matchRoutes match
    const matchRoutesMatch = item.matchRoutes?.some(r => route.path.startsWith(r)) ?? false
    
    if (!routeMatches && !matchRoutesMatch) {
      return currentBest
    }

    if (!currentBest) {
      return item
    }

    // Determine effective match length for sorting
    const currentLength = currentBest.route?.length ?? 0
    let candidateLength = item.route?.length ?? 0
    
    // matchRoutes get priority boost for exact matches
    if (matchRoutesMatch) {
      const matchedRoute = item.matchRoutes?.find(r => route.path.startsWith(r))
      if (matchedRoute && matchedRoute.length > candidateLength) {
        candidateLength = matchedRoute.length
      }
    }

    if (candidateLength > currentLength) {
      return item
    }

    return currentBest
  }, null)

  if (bestMatch) {
    return bestMatch.name
  }

  return secondaryNavigationItems.value[0]?.name ?? null
})

const notificationsCount = computed(() => notifications.unreadCount.value)
const { walletBalanceLabel, loading: profileLoading, profile, avatarUrl } = storeToRefs(profileStore)
const avatarPhoto = computed(() => avatarUrl.value)
const isAuthenticated = computed(() => !!profile.value)

const showLogin = ref(false)
const showRegistration = ref(false)
const loginEmail = ref('')
const recoveryEmail = ref('')
const autoSendCode = ref(false)

const loginTotp = ref<TelegramTotpSignal | null>(null)

const openLogin = () => {
  showRegistration.value = false
  showRecovery.value = false
  loginEmail.value = ''
  autoSendCode.value = false
  loginTotp.value = null
  showLogin.value = true
}

// OTP-R6c: pending-вход через Telegram из окна регистрации — шаг кода из приложения в окне входа.
const openLoginWithTotp = (signal: TelegramTotpSignal) => {
  showRegistration.value = false
  showRecovery.value = false
  loginEmail.value = ''
  autoSendCode.value = false
  loginTotp.value = signal
  showLogin.value = true
}

const openLoginWithCode = (email: string) => {
  showRegistration.value = false
  showRecovery.value = false
  loginEmail.value = email
  autoSendCode.value = true
  loginTotp.value = null
  showLogin.value = true
}

const openRegistration = () => {
  showLogin.value = false
  showRecovery.value = false
  showRegistration.value = true
}

const showRecovery = ref(false)

const openRecovery = (email?: string) => {
  showLogin.value = false
  showRegistration.value = false
  recoveryEmail.value = email || ''
  showRecovery.value = true
}

const { t } = useI18n()

const walletTooltip = computed(() => {
  if (profileLoading.value) {
    return t('wallet.loadingTooltip')
  }

  return walletBalanceLabel.value ? t('wallet.balanceTooltip', { balance: walletBalanceLabel.value }) : t('nav.wallet')
})

const profileInitials = computed(() => {
  const name = profileStore.fullName

  if (!name) {
    return 'GG'
  }

  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2)

  return initials || 'GG'
})

const layoutStore = useLayoutStore()

const handleToggle = () => {
  layoutStore.toggleSidebar()
}

const handleMainNavigate = async (item: NavigationItem) => {
  if (item.disabled || !item.route) {
    return
  }

  const hashIndex = item.route.indexOf('#')
  if (hashIndex === -1) {
    router.push({ path: item.route })
    return
  }

  // Anchor item (guest landing sections): make sure we are on the landing,
  // then scroll to the section once it is rendered (lazy route chunk).
  const selector = item.route.slice(hashIndex)
  if (route.path !== '/') {
    await router.push({ path: '/' })
  }
  let attempts = 0
  const tryScroll = () => {
    const el = document.querySelector(selector)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else if (attempts++ < 20) {
      requestAnimationFrame(tryScroll)
    }
  }
  tryScroll()
}

const handleSecondaryNavigate = (item: NavigationItem) => {
  if (item.disabled || !item.route) {
    return
  }

  const newQuery = { ...route.query }
  delete newQuery.tab
  router.push({ path: item.route, query: newQuery })
}

const isMainActive = (item: NavigationItem) => activeMain.value?.name === item.name

const handleNotificationsClick = () => {
  if (profileStore.needsSponsor) {
    profileStore.sponsorPromptOpen = true
    return
  }
  if (!isAuthenticated.value) {
    return
  }
  void notifications.openPanel()
}

const maybeRedirectToDashboard = () => {
  if (route.name === 'landing' && profile.value) {
    router.replace({ name: 'dashboard' })
  }
}

const ensureAuthToken = (): string | null => {
  const token = tokenStorage.getToken()
  if (token) {
    return token
  }

  return null
}

const bootstrapAuthState = async () => {
  const token = ensureAuthToken()
  if (!token) {
    return
  }

  // In TMA mode, don't fetch profile until TMA auth completes.
  // TmaInitPage handles the full auth → fetchProfile → redirect flow.
  if (isInTelegram && tmaState?.value && tmaState.value.status !== 'done') {
    return
  }

  // Load notifications immediately if token exists, don't wait for profile
  void notifications.loadNotifications(true)

  try {
    if (!profile.value && !profileStore.loading) {
      await profileStore.fetchProfile()
    }
    maybeRedirectToDashboard()
  } catch (error) {
    console.warn('[GonkaGame][AppHeader] bootstrap failed', error)
  }
}

watch(profile, (newProfile, oldProfile) => {
  maybeRedirectToDashboard()
  // Load notifications when user becomes authenticated (login/register)
  if (newProfile && !oldProfile) {
    void notifications.loadNotifications(true)
  }
})

// Reload notifications on route change if authenticated
watch(
  () => route.path,
  () => {
    const token = tokenStorage.getToken()
    if (token) {
      void notifications.loadNotifications(true)
    }
  },
)

onMounted(() => {
  void bootstrapAuthState()
  adminReturnToken.value = localStorage.getItem('admin_return_token')
})

const adminReturnToken = ref<string | null>(null)

const returnToAdmin = () => {
  if (adminReturnToken.value) {
    tokenStorage.setToken(adminReturnToken.value)
    localStorage.removeItem('admin_return_token')
    window.location.href = '/dashboard'
  }
}
</script>

<style scoped lang="scss">
.app-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--hm-surface);
  border-bottom: 1px solid var(--hm-border);
  position: sticky;
  top: 0;
  z-index: 50;
  // GPU acceleration to prevent scroll flickering
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 32px;
    gap: 24px;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 24px;
    flex: 1;
  }

  &__burger {
    display: none;
    border: none;
    background: transparent;
    padding: 8px;
    border-radius: var(--ds-radius-control);
    transition: background 0.2s;

    span {
      display: block;
      width: 22px;
      height: 2px;
      background: var(--hm-text-secondary);
      border-radius: var(--ds-radius-pill);

      &:not(:last-child) {
        margin-bottom: 5px;
      }
    }

    &:hover {
      background: var(--ds-accent-soft-08);
    }
  }

  &__brand {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 140px;
    min-height: 44px;
    text-decoration: none;

    &:focus-visible {
      outline: 2px solid var(--hm-accent);
      outline-offset: 4px;
    }
  }

  &__logo {
    width: 128px;
    height: 48px;
  }

  &__menu {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
    @media screen and (max-width: 1100px) {
      font-size: var(--ds-text-size-body);
      gap: 0px;
    }
  }

  &__menu-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 0;
    border: 1px solid transparent;
    background: transparent;
    color: var(--hm-text-secondary);
    font-weight: var(--ds-weight-medium);
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
    min-height: 48px;
    font-family: var(--ds-font-ui);
    font-size: 16px;

    &:hover {
      background: var(--ds-accent-soft-08);
      color: var(--hm-text-primary);
    }

    @media screen and (max-width: 1100px) {
      padding: 10px 8px;
    }
  }

  &__menu-item--active {
    color: var(--ds-text);
    border-bottom: 3px solid var(--ds-text);
    background: transparent;
  }

  &__menu-item--disabled {
    cursor: default;
    opacity: 0.5;

    &:hover {
      background: transparent;
      color: var(--hm-text-secondary);
    }
  }

  &__menu-tag {
    font-size: var(--p-text-10);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--hm-accent);
    background: var(--ds-accent-soft-12);
    border-radius: var(--ds-radius-pill);
    padding: 2px 6px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__icon-button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: var(--p-radius-50);
    border: 1px solid var(--hm-border);
    background: var(--hm-surface-alt);
    color: var(--hm-text-secondary);
    text-decoration: none;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

    &:hover {
      background: var(--ds-brand-accent-soft);
      color: var(--ds-brand-accent);
      border-color: var(--ds-brand-accent-border);
    }
  }

  &__icon-button--loading {
    pointer-events: none;
    color: var(--hm-text-muted);
  }

  &__icon-font {
    font-size: var(--ds-text-size-h-sm);
    line-height: 1;
  }

  &__spinner {
    width: 18px;
    height: 18px;
    border: 2px solid var(--ds-accent-soft-20);
    border-top-color: var(--hm-accent);
    border-radius: 50%;
    animation: app-header-spin 0.8s linear infinite;
  }

  &__icon-dot {
    position: absolute;
    top: -6px;
    right: -6px;
    background: var(--hm-danger);
    color: var(--hm-text-primary);
    border-radius: var(--ds-radius-pill);
    padding: 2px 6px;
    font-size: var(--p-text-10);
    font-weight: var(--ds-weight-semibold);
    line-height: 1;
    min-width: 18px;
    text-align: center;
    box-shadow: 0 0 0 2px var(--hm-surface);
    animation: badge-pulse 2s ease-in-out infinite;
  }

  &__avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid var(--ds-accent-soft-16);
    box-shadow: 0 6px 18px var(--ds-accent-soft-16);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--hm-surface-alt);
    padding: 0;
    line-height: 0;
    text-decoration: none;
    cursor: pointer;
    transition: transform 0.15s ease;

    &:hover {
      transform: translateY(-1px);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  &__profile-wrapper {
    position: relative;

    &:hover .app-header__admin-dropdown {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }

  &__admin-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 8px;
    background: var(--hm-surface);
    border: 1px solid var(--hm-border);
    border-radius: var(--ds-radius-card);
    padding: 8px;
    box-shadow: var(--hm-shadow-lg);
    min-width: 180px;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.2s ease;
    z-index: 100;
  }

  &__admin-return-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 12px;
    border: none;
    background: transparent;
    color: var(--hm-text-secondary);
    border-radius: var(--ds-radius-control);
    cursor: pointer;
    font-size: var(--ds-text-size-body);
    font-weight: var(--ds-weight-medium);
    transition: all 0.2s;

    &:hover {
      background: var(--ds-accent-soft-08);
      color: var(--hm-accent);
    }

    i {
      font-size: var(--p-text-18);
    }
  }

  &__avatar-skeleton {
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, var(--ds-overlay-04), var(--ds-overlay-10), var(--ds-overlay-04));
    animation: app-header-shimmer 1.2s ease-in-out infinite;
  }

  &__avatar-placeholder {
    font-size: var(--ds-text-size-body-lg);
    font-weight: var(--ds-weight-semibold);
    color: var(--hm-text-secondary);
  }

  &__avatar--disabled {
    cursor: default;
    transform: none;
    opacity: 0.7;
    pointer-events: none;
  }

  &__secondary {
    border-top: 1px solid var(--hm-border);
    padding: 0 32px;
    background: var(--hm-surface);
  }

  &__tabs {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 12px 0;
  }

  &__tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: var(--ds-radius-pill);
    border: 1px solid transparent;
    background: transparent;
    color: var(--hm-text-secondary);
    font-weight: var(--ds-weight-medium);
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

    &:hover {
      background: var(--ds-accent-soft-08);
      color: var(--hm-text-primary);
    }
  }

  &__tab--active {
    border-color: var(--ds-accent-soft-18);
    background: var(--ds-accent-soft-12);
    color: var(--hm-accent);
  }

  &__tab--disabled {
    cursor: default;
    opacity: 0.5;

    &:hover {
      background: transparent;
      color: var(--hm-text-secondary);
    }
  }

  &__tab-badge {
    font-size: var(--p-text-10);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--hm-text-muted);
  }

  @media screen and (max-width: 1280px) {
    &__menu {
      gap: 8px;
    }

    &__actions {
      gap: 12px;
    }
  }
  @media screen and (max-width: 1100px) {
    // Hide disabled items to save space in compact mode
    &__menu-item--disabled {
      display: none;
    }
  }
  @media screen and (max-width: 1024px) {
    &__top {
      padding: 16px 24px;
    }

    // Compact menu for tablet landscape range (769px - 1024px)
    &__menu-item {
      padding: 8px 10px;
      font-size: var(--p-text-13);
    }



    // Hide secondary tabs on tablet/mobile - covered by Bottom Nav
    &__secondary {
      display: none;
    }

    &__secondary {
      padding: 0 24px;
    }

    &__tabs {
      overflow-x: auto;
    }
  }

  @media screen and (max-width: 768px) {
    // Menu hidden in mobile/tablet portrait - navigation handled by BottomNav
    &__menu {
      display: none;
    }
  }

  @media screen and (max-width: 640px) {
    &__wallet {
      display: none;
    }

    &__top {
      gap: 16px;
    }
  }
}

@keyframes app-header-spin {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes badge-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}

@keyframes app-header-shimmer {
  0% {
    background-position: -40px;
  }

  100% {
    background-position: 60px;
  }
}

.auth-btn-text {
  background: transparent;
  border: none;
  color: var(--hm-accent);
  font-weight: var(--ds-weight-semibold);
  font-size: var(--ds-text-size-body);
  cursor: pointer;
  padding: 8px 16px;
  
  &:hover {
    color: var(--hm-text-primary);
  }
}

.auth-btn-primary {
  background: var(--hm-accent);
  color: var(--hm-text-primary);
  border: none;
  border-radius: var(--p-radius-50);
  padding: 8px 20px;
  font-weight: var(--ds-weight-semibold);
  font-size: var(--ds-text-size-body);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  
  &:hover {
    background: var(--hm-accent-light);
  }
}
</style>
